import { useState } from 'react';
import { useSalePrefix } from '../lib/listingPaths';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useMutation } from '../lib/useMutation';
import { money, shortDate } from '../lib/format';
import {
  Alert, Button, Card, ErrorBlock, Field, LoadingBlock, TextInput,
} from '../components/ui';

/**
 * Buying the publication slot that puts a sale listing on the site.
 *
 * The screen has to be honest about a thing sellers find surprising: pressing
 * the button here does not publish anything. Money arrives at a Till, a person
 * confirms it, and the listing goes live then — the same shape a subscription
 * payment already has. So the primary button says what it does ("Tell us I
 * have paid"), and the waiting state says plainly that somebody is checking.
 *
 * Three states, and the screen picks one rather than showing all three:
 *   nothing priced   → one button, publishes free, and says why it was free
 *   a payment waits  → what was quoted, and a way to take it back
 *   otherwise        → the rate card, the Till, and the reference field
 */

interface Rate {
  code: string; name: string; description: string | null; days: number;
  price: string; currency_code: string;
  tier: number; tier_name: string; homepage: boolean; max_active: number | null;
  is_featured: boolean; once_per_account: boolean;
  available: boolean; free: boolean; spent: boolean; full: boolean;
  next_free_at: string | null;
}

interface Pending {
  id: string; rate_code: string | null; rate_name: string | null; tier: number;
  homepage: boolean; amount: string; currency_code: string;
  days: number; payment_reference: string | null; requested_at: string;
}

interface History {
  source: string; state: string; amount: string; days: number;
  rate_name: string | null; tier: number; homepage: boolean;
  covers_from: string | null; covers_until: string | null;
  grant_reason: string | null; rejected_reason: string | null;
  refund_reason: string | null; requested_at: string;
}

/**
 * What a tier actually does, in the buyer's terms rather than the price list's.
 *
 * Derived from the two things the platform genuinely changes — where the advert
 * sits, and whether it can reach the showcase strip — and nothing else. The
 * specification also lists an "enhanced gallery" and video under the top tier;
 * every sale listing already gets twelve photographs, 360° tours and a video,
 * so saying so here would mean either charging for what is free or taking it
 * away from everyone below. Neither is worth doing to fill out a price card.
 */
function includes(r: Rate): string[] {
  const lines = ['Photos, 360° tours and video, as every listing gets'];
  if (r.tier >= 3) lines.unshift('Sits at the top of Buy Property search');
  else if (r.tier === 2) lines.unshift('Sits above ordinary listings in Buy Property search');
  else lines.unshift('Appears in Buy Property search');
  if (r.homepage) lines.splice(1, 0, 'Can take a place on the showcase strip, while places last');
  return lines;
}

interface Info {
  package: {
    name: string | null; remaining: number; canUse: boolean; maxListings: number;
    coversUntil: string; daysLeft: number; tier: number;
  } | null;
  listing: {
    title: string; status: string;
    placementTier: number; placementUntil: string | null; homepageUntil: string | null;
  };
  gates: {
    reviewed: boolean; paid: boolean; paidUntil: string | null;
    publishable: boolean; blockedBy: 'review' | 'payment' | null;
  };
  rates: Rate[];
  tierNames: Record<string, string>;
  ratesConfigured: boolean;
  freeSlot: { days: number; reason: string } | null;
  payInfo: { tillNumber: string; tillName: string | null; instructions: string | null } | null;
  pending: Pending | null;
  history: History[];
}

/** Why a slot cost nothing, in words a seller would use. */
function freeBecause(reason: string | null, rates: Rate[]): string {
  if (reason === 'no_rate_configured') return 'Publishing is free at the moment.';
  const code = reason?.startsWith('rate:') ? reason.slice(5) : null;
  const rate = code ? rates.find((r) => r.code === code) : null;
  return rate ? rate.name : 'Given by our team.';
}

export function SaleListingPay() {
  const salePrefix = useSalePrefix();
  const { id } = useParams<{ id: string }>();
  const view = useApi<Info>(`/api/v1/sale-listings/${id}/payment`);
  const act = useMutation<{ free?: boolean; status?: string }>();

  const [chosen, setChosen] = useState<string>('');
  const [reference, setReference] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  if (view.loading) return <div className="page"><Card><LoadingBlock /></Card></div>;
  if (view.error) {
    return (
      <div className="page">
        <Card><ErrorBlock error={view.error} onRetry={view.reload} /></Card>
      </div>
    );
  }

  const d = view.data!;
  const paidRates = d.rates.filter((r) => !r.free);
  const freeRates = d.rates.filter((r) => r.free && r.available);
  const selected = d.rates.find((r) => r.code === chosen);

  const submit = async (rateCode?: string) => {
    setNotice(null);
    const body: Record<string, unknown> = {};
    if (rateCode) body.rateCode = rateCode;
    if (!rateCode && chosen) body.rateCode = chosen;
    if (reference.trim()) body.paymentReference = reference.trim();
    const res = await act.run(`/api/v1/sale-listings/${id}/payment`, { body });
    setReference('');
    setNotice(res.free
      ? (res.status === 'published'
        ? 'Your property is now live on Buy Property.'
        : 'The publication slot is yours. It goes live as soon as review is done.')
      : 'Thank you. We will confirm the payment and put the listing live, usually within a working day.');
    view.reload();
  };

  /*
   * Deliberately sends NO rateCode: naming a rate is how a seller says "charge
   * me for this one", so the absence is how they say "use what I have already
   * paid for". The server makes the same distinction.
   */
  const submitPackage = async () => {
    setNotice(null);
    const res: any = await act.run(`/api/v1/sale-listings/${id}/payment`, { body: {} });
    setNotice(res.status === 'published'
      ? 'Your property is now live on Buy Property, using one of your places.'
      : 'That place is held. It goes live as soon as review is done.');
    view.reload();
  };

  const withdraw = async () => {
    setNotice(null);
    await act.run(`/api/v1/sale-listings/${id}/payment`, { method: 'DELETE' });
    setNotice('Withdrawn. You can quote a different M-Pesa code now.');
    view.reload();
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Publish “{d.listing.title}”</h1>
          <p className="page-sub">
            A listing goes live once our team has approved it and it holds a publication slot.
          </p>
        </div>
        <div className="row">
          {/* Beside publishing, because "how do I look trustworthy" is the
              question a seller asks in the same sitting as "how do I go live". */}
          <Link to={`${salePrefix}/${id}/verification`}>Get it checked</Link>
          <Link to={`${salePrefix}/${id}`}>Back to the listing</Link>
        </div>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {act.error && <Alert tone="danger">{act.error.message}</Alert>}

      {/* Both gates, always, so the seller can see which one they are looking at. */}
      <Card title="Where this listing has got to">
        <ul className="plan-list">
          <li>
            {d.gates.reviewed ? '✓' : '•'}{' '}
            {d.gates.reviewed
              ? 'Reviewed and approved by our team.'
              : 'Waiting for our team to review it.'}
          </li>
          <li>
            {d.gates.paid ? '✓' : '•'}{' '}
            {d.gates.paid
              ? `Publication slot runs until ${shortDate(d.gates.paidUntil!)}.`
              : 'No publication slot yet.'}
          </li>
          {/* Only when they hold one: an ordinary listing should not be told
              every week that it is ordinary. */}
          {d.listing.placementTier > 1 && d.listing.placementUntil && (
            <li>
              ✓ {d.tierNames[String(d.listing.placementTier)] ?? 'Raised'} placement
              until {shortDate(d.listing.placementUntil)}
              {d.listing.homepageUntil && new Date(d.listing.homepageUntil) > new Date()
                && <>, with a showcase place until {shortDate(d.listing.homepageUntil)}</>}.
            </li>
          )}
        </ul>
        {d.gates.publishable && (
          <Alert tone="success">Both done — this listing is on the site.</Alert>
        )}
      </Card>

      {/*
        * A package the seller already holds pays for this, so it is offered
        * FIRST and the paid ladder stays below it — somebody who has bought
        * thirty places should not have to read a price list to use one. The
        * ladder is still there, because buying a boost for one property out of
        * thirty is a real thing to want.
        */}
      {d.package && !d.gates.paid && !d.pending && (
        <Card title={`Covered by ${d.package.name ?? 'your package'}`}>
          {d.package.canUse ? (
            <>
              <p>
                {d.package.remaining} of {d.package.maxListings} places free.
                Publishing this property uses one and costs nothing more; it runs
                until {shortDate(d.package.coversUntil)}, with the rest of your adverts.
              </p>
              <Button onClick={() => submitPackage()} disabled={act.busy}>
                {act.busy ? 'Working…' : 'Publish with my package'}
              </Button>
            </>
          ) : (
            <Alert tone="warning">
              All {d.package.maxListings} places are in use. Take another advert down to
              free one, buy a bigger package, or pay for this property on its own below.
            </Alert>
          )}
        </Card>
      )}

      {d.pending ? (
        <Card title="We are checking your payment">
          <p>
            You told us you paid {money(d.pending.amount)} for{' '}
            {d.pending.rate_name ?? `${d.pending.days} days`}
            {d.pending.tier > 1 && <> ({d.tierNames[String(d.pending.tier)]})</>}
            {d.pending.payment_reference && <> quoting <strong>{d.pending.payment_reference}</strong></>}
            {' '}on {shortDate(d.pending.requested_at)}.
          </p>
          <p className="muted">
            Nothing changes on the site until somebody here has matched that code to the
            money. If you quoted the wrong code, withdraw this and send the right one.
          </p>
          <Button variant="secondary" onClick={withdraw} disabled={act.busy}>
            Withdraw and start again
          </Button>
        </Card>
      ) : !d.ratesConfigured ? (
        <Card title="Publishing is free at the moment">
          <p>
            We are not charging for sale listings yet, so this one can go on the site
            for {d.freeSlot?.days ?? 60} days at no cost.
          </p>
          <Button onClick={() => submit()} disabled={act.busy}>
            {act.busy ? 'Working…' : 'Put it on the site'}
          </Button>
        </Card>
      ) : (
        <>
          {freeRates.map((r) => (
            <Card key={r.code} title={r.name}>
              <p>
                {r.days} days on Buy Property, free
                {r.once_per_account && <> — one per account</>}.
              </p>
              <Button onClick={() => submit(r.code)} disabled={act.busy}>
                {act.busy ? 'Working…' : 'Use it'}
              </Button>
            </Card>
          ))}

          <Card title="Choose how your property is advertised">
            {paidRates.length === 0
              ? <p className="muted">No paid options are on offer right now.</p>
              : (
                <div className="plan-grid">
                  {paidRates.map((r) => (
                    <label
                      key={r.code}
                      className={`plan-card${chosen === r.code ? ' current' : ''}`}
                    >
                      <span className="plan-name">
                        <input
                          type="radio" name="rate" value={r.code}
                          checked={chosen === r.code}
                          disabled={!r.available}
                          onChange={() => setChosen(r.code)}
                        />{' '}
                        {r.name}
                      </span>
                      <span className="plan-price">{money(r.price)}</span>
                      <span className="muted">
                        {r.days} days{r.tier > 1 ? ` · ${r.tier_name}` : ''}
                      </span>
                      {r.description && <span className="muted">{r.description}</span>}
                      <ul className="plan-list">
                        {includes(r).map((line) => <li key={line}>{line}</li>)}
                      </ul>
                      {/* Two different refusals, two different sentences: one is
                          about this account, the other about the strip being
                          finite — and only the second has a date to wait for. */}
                      {r.spent && <span className="muted">Already used by this account</span>}
                      {r.full && (
                        <span className="muted">
                          Showcase full{r.next_free_at ? ` until ${shortDate(r.next_free_at)}` : ''}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
          </Card>

          {d.payInfo ? (
            <Card title="How to pay">
              <p>
                Pay <strong>{selected ? money(selected.price) : 'the amount above'}</strong> to
                M-Pesa Till <strong>{d.payInfo.tillNumber}</strong>
                {d.payInfo.tillName && <> ({d.payInfo.tillName})</>}, then enter the
                confirmation code from the M-Pesa message below.
              </p>
              {d.payInfo.instructions && <p className="muted">{d.payInfo.instructions}</p>}
              <Field label="M-Pesa confirmation code" htmlFor="ref">
                <TextInput
                  id="ref" value={reference} placeholder="e.g. SFH4K2LMN9"
                  onChange={(e) => setReference(e.target.value)}
                />
              </Field>
              <Button
                onClick={() => submit()}
                disabled={act.busy || !chosen || reference.trim().length < 4}
              >
                {act.busy ? 'Sending…' : 'Tell us I have paid'}
              </Button>
              <p className="muted" style={{ marginTop: 8 }}>
                We check the code against the payment and put the listing live. It does
                not go on the site before that.
              </p>
            </Card>
          ) : (
            <Card title="How to pay">
              <Alert tone="warning">
                Online payment is not set up yet. Please contact us and we will publish
                this listing for you.
              </Alert>
            </Card>
          )}
        </>
      )}

      {d.history.length > 0 && (
        <Card title="Publication history" bodyClassName="card-pad-0">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th><th>What</th><th className="num">Amount</th>
                  <th>Covers</th><th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {d.history.map((h, i) => (
                  <tr key={i}>
                    <td>{shortDate(h.requested_at)}</td>
                    <td>
                      {h.source === 'operator_grant'
                        ? freeBecause(h.grant_reason, d.rates)
                        : `${h.rate_name ?? `${h.days} days`}`}
                      {h.tier > 1 && <span className="muted"> · {d.tierNames[String(h.tier)]}</span>}
                    </td>
                    <td className="num">{money(h.amount)}</td>
                    <td>{h.covers_until ? `until ${shortDate(h.covers_until)}` : '—'}</td>
                    <td>
                      {h.state === 'confirmed' ? 'Confirmed'
                        : h.state === 'rejected' ? `Not accepted — ${h.rejected_reason ?? ''}`
                          : h.state === 'refunded' ? `Refunded — ${h.refund_reason ?? ''}`
                            : h.state}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
